import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/hooks/use-toast'
import { openAttachmentInNewTab, useAttachmentObjectUrl } from '@/hooks/useFeedback'
import type { FeedbackAttachment } from '@/types/feedback'

interface AttachmentPreviewProps {
  reportId: string
  attachment: FeedbackAttachment
}

export function AttachmentPreview({ reportId, attachment }: AttachmentPreviewProps) {
  const { t } = useTranslation('feedback')
  const [open, setOpen] = useState(false)
  const isImage = attachment.content_type.startsWith('image/')
  const url = useAttachmentObjectUrl(reportId, attachment.id, isImage)

  if (!isImage) {
    const handleOpen = () => {
      openAttachmentInNewTab(reportId, attachment.id).catch(() =>
        toast({ variant: 'destructive', title: t('detail.openError') }),
      )
    }
    return (
      <button
        type="button"
        onClick={handleOpen}
        aria-label={t('detail.openAttachment', { name: attachment.filename })}
        className="flex h-32 flex-col items-center justify-center gap-2 rounded-md border p-2 text-sm hover:bg-muted"
      >
        <FileText className="h-8 w-8 text-muted-foreground" />
        <span className="w-full truncate text-center">{attachment.filename}</span>
      </button>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('detail.enlarge', { name: attachment.filename })}
        className="h-32 overflow-hidden rounded-md border hover:opacity-90"
      >
        {url ? (
          <img
            src={url}
            alt={attachment.filename}
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <Skeleton className="h-full w-full" />
        )}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" aria-describedby={undefined}>
          <DialogTitle className="truncate pr-6">{attachment.filename}</DialogTitle>
          {url && (
            <img src={url} alt={attachment.filename} className="max-h-[80vh] w-full object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
